import type { CalendarEventRead, CampaignRead, PlannedPostRead } from '../../types/api';
import { dayLabel } from './calendarDates';
import { DayEntryList, type DayEntries } from './UpcomingList';

interface DayAgendaProps {
  /** The day picked on the dot grid, with everything filed under it. */
  day: DayEntries;
  campaignsById: ReadonlyMap<string, CampaignRead>;
  onOpenPost: (post: PlannedPostRead) => void;
  onOpenEvent: (event: CalendarEventRead) => void;
  onResetPost: (post: PlannedPostRead) => void;
  /** Plan a new post on this day. */
  onPlanPost: (iso: string) => void;
}

/**
 * The phone's day under the month (MOBILE_RESPONSIVE_PLAN.md ruling 11).
 *
 * At phone width the month grid is a picker of day numbers and dots; this is
 * where the picked day is read, in the list view's own rows at full width,
 * and where a post is planned onto it. `?day=` decides the day, today when
 * nothing is picked.
 */
export function DayAgenda({
  day,
  campaignsById,
  onOpenPost,
  onOpenEvent,
  onResetPost,
  onPlanPost,
}: DayAgendaProps) {
  const label = dayLabel(day.iso);
  const isEmpty = day.events.length === 0 && day.posts.length === 0;

  return (
    <section className="day-agenda" aria-labelledby="day-agenda-title">
      {/* Polite: picking a day on the grid says which day the list below is. */}
      <h3 className="day-agenda__date" id="day-agenda-title" aria-live="polite">
        {label}
      </h3>

      {isEmpty ? (
        <p className="day-agenda__empty">Nothing planned on this day.</p>
      ) : (
        <DayEntryList
          day={day}
          campaignsById={campaignsById}
          onOpenPost={onOpenPost}
          onOpenEvent={onOpenEvent}
          onResetPost={onResetPost}
        />
      )}

      <button className="button day-agenda__plan" type="button" onClick={() => onPlanPost(day.iso)}>
        Plan a post on {label}
      </button>
    </section>
  );
}
